import React, { useEffect, useState, useMemo } from 'react'
import { Platform } from 'react-native'
import CopyrightFooter from '../../components/Footers/CopyrightFooter'
import { TriangleHeader } from '../../components/Headers/TriangleHeader'
import Separator from '../../components/Separator'
import { Text, View } from '../../components/Themed'
import Colors from '../../constants/Colors'
import GlobalStyle, {
	formStyle,
	mediumTextSize,
	useGlobalStyles,
} from '../../constants/GlobalStyle'
import { auth } from '../../firebase/config'
import { useAppDispatch, useAppSelector } from '../../state/hooks'
import BuyGiftCardPrompt from './views/BuyGiftCardPrompt'
import ExistingGiftCardMenu from './views/ExistingGiftCardMenu'
import GiftCardSelector from './views/GiftCardSelector'
import RecipientForm from './views/RecipientForm'
// import ScheduleGiftCard from './views/ScheduleGiftCard'
import { resetCheckoutState, setCheckoutState } from '../../state/Slices/checkoutSlice'
import { DEFAULT_GIFT_CARD_DEMONINATIONS } from './constants'
import { GiftCardOptions, GiftCardType, Recipient } from './types'
import OrderSummary from './views/OrderSummary'
import { Box, Inline, Stack, useBreakpoints, Button } from '../../components/DesignSystem'
import { Form, FormProvider } from 'react-hook-form'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useOrganizationStyles } from '../../hooks/useOrganizationStyles'
import parsePhoneNumberFromString from 'libphonenumber-js'

type RecipientFormData = {
	recipientType: GiftCardType
	amount: string
	senderName: string
	recipientName: string
	recipientEmail: string
	recipientPhone: string
	customMessage: string
}

const DEFAULT_GIFT_CARD_OPTIONS = {
	recipientType: undefined,
	amount: undefined,
	quantity: 1,
	purchaserId: undefined,
	senderName: undefined,
	customMessage: undefined,
	recipientPhone: undefined,
	recipient: null,
}

const giftCardSchema = z.discriminatedUnion('recipientType', [
	z.object({
		recipientType: z.literal(GiftCardType.Personal),
		amount: z.string().min(1),
		senderName: z.string().optional(),
		recipientName: z.string().optional(),
		recipientEmail: z.string().optional(),
		recipientPhone: z.string().optional(),
		customMessage: z.string().optional(),
		quantity: z.number().optional(),
	}),
	z.object({
		recipientType: z.literal(GiftCardType.Gift),
		amount: z.string().min(1),
		senderName: z.string().min(1, 'Sender name is required'),
		recipientName: z.string().min(1, 'Recipient name is required'),
		recipientEmail: z.string().email('Invalid email address'),
		recipientPhone: z.string().refine(value => {
			const cleanedValue = value?.replace(/\D/g, '')
			if (!cleanedValue || cleanedValue.length !== 10) {
				return false
			}
			const phoneNumber = parsePhoneNumberFromString(value, 'CA')
			return phoneNumber && phoneNumber.isValid() && phoneNumber.country === 'CA'
		}, 'Please enter a valid Canadian phone number'),
		customMessage: z.string().optional(),
		quantity: z.number().optional(),
	}),
])

export type GiftCardFormData = z.infer<typeof giftCardSchema>

// Refactor with ApplyGiftCardForm.tsx.
export default function GiftCardsScreen({ navigation }) {
	const dispatch = useAppDispatch()
	const { organizationLogo } = useAppSelector(state => state.data)
	const { deviceHasInternet } = useAppSelector(state => state.user)

	const [loading, setLoading] = useState(true)
	const [showOrderSummary, setShowOrderSummary] = useState(false)
	const [giftCardOptions, setGiftCardOptions] = useState<GiftCardOptions | null>(
		DEFAULT_GIFT_CARD_OPTIONS
	)
	const { isMobile } = useBreakpoints()
	const { shadowContainer } = useGlobalStyles()
	const { appBackground } = useOrganizationStyles()
	// TODO: Remove this once we confirm the purchase flow is working.
	const showPurchaseFlow = true

	const methods = useForm<GiftCardFormData>({
		resolver: zodResolver(giftCardSchema),
		defaultValues: DEFAULT_GIFT_CARD_OPTIONS,
	})

	const {
		watch,
		control,
		handleSubmit,
		formState: { errors },
		setValue,
		reset,
	} = methods
	const formValues = watch()

	const onSubmit = (data: GiftCardFormData) => {
		const { recipientName, recipientEmail, recipientPhone, ...rest } = data

		const amount = Number(data.amount)

		const formattedData =
			data.recipientType === GiftCardType.Gift
				? {
						...rest,
						amount,
						scheduledAt: undefined,
						purchaserId: undefined,
						recipient: {
							name: recipientName,
							email: recipientEmail,
							phoneNumber: recipientPhone,
						},
					}
				: {
						...data,
						amount,
						purchaserId: undefined,
						recipient: null,
					}

		setShowOrderSummary(true)
		dispatch(setCheckoutState({ products: [formattedData] }))
	}

	const resetGiftCardSelections = () => {
		reset()
		dispatch(resetCheckoutState())
	}

	//   This is the current addProduct function.

	const isGift = formValues.recipientType === GiftCardType.Gift

	const formattedGiftCardOptions = useMemo(
		() => ({
			recipientType: formValues.recipientType,
			amount: Number(formValues.amount),
			quantity: formValues.quantity ?? 1,
			purchaserId: undefined,
			senderName: formValues.senderName,
			customMessage: formValues.customMessage,
			recipient:
				formValues.recipientType === GiftCardType.Gift
					? {
							name: formValues.recipientName,
							email: formValues.recipientEmail,
							phoneNumber: formValues.recipientPhone,
							scheduledAt: undefined,
						}
					: null,
		}),
		[formValues]
	)

	useEffect(() => {
		if (formValues.recipientType === GiftCardType.Personal) {
			setValue('senderName', undefined)
			setValue('recipientName', undefined)
			setValue('recipientEmail', undefined)
			setValue('recipientPhone', undefined)
			setValue('customMessage', undefined)
		}
	}, [formValues.recipientType, methods])

	return (
		<>
			<TriangleHeader
				logo={organizationLogo}
				background={!loading && auth.currentUser ? 'white' : appBackground}
				navigation={navigation}
				title={'GIFT CARDS'}
				backFunction={
					Platform.OS === 'web'
						? () => {
								if (navigation.canGoBack()) {
									navigation.goBack()
								} else {
									navigation.navigate('HomeStack', { screen: 'RestaurantSelect' })
								}
							}
						: null
				}
			/>
			<Box maxWidth="medium" marginX="md" marginY={isMobile ? 'md' : 'lg'} style={{ flexGrow: 1 }}>
				<View
					style={[
						shadowContainer,
						{
							display: 'flex',
							flexDirection: 'column',
							backgroundColor: Colors.custom.white,
							marginHorizontal: 8,
							padding: isMobile ? 24 : 32,
						},
					]}
				>
					<Stack gap="xxl" align={{ mobile: 'left', tablet: 'left', desktop: 'left' }}>
						<Text style={GlobalStyle.title}>Gift Cards</Text>
						{!showOrderSummary && (
							<View
								style={{
									display: 'flex',
									flexDirection: 'column',
									width: '100%',
								}}
							>
								{showPurchaseFlow && (
									<FormProvider {...methods}>
										<Form control={control}>
											<Stack gap="xxl">
												{/* Select whether the gift card is for yourself or someone else. */}
												<BuyGiftCardPrompt
													deviceHasInternet={deviceHasInternet}
													control={control}
													name="recipientType"
												/>

												{/* Select the amount of the gift card. */}
												{formValues.recipientType && (
													<GiftCardSelector
														name="amount"
														control={control}
														deviceHasInternet={deviceHasInternet}
														denominations={DEFAULT_GIFT_CARD_DEMONINATIONS}
													/>
												)}

												{isGift && <RecipientForm control={control} />}

												{/* Show review and checkout button if the user has selected a gift card type. */}
												{formValues.recipientType && (
													<Inline
														alignX={{
															mobile: 'center',
															tablet: isGift ? 'right' : 'center',
															desktop: isGift ? 'right' : 'center',
														}}
													>
														<Button
															fullWidth={isMobile}
															disabled={!deviceHasInternet}
															onPress={handleSubmit(onSubmit)}
														>
															Review & Confirm
														</Button>
													</Inline>
												)}

												<Separator />
											</Stack>
										</Form>
									</FormProvider>
								)}
								<ExistingGiftCardMenu deviceHasInternet={deviceHasInternet} />
							</View>
						)}
						{/* Next page as a component. */}
						{showOrderSummary && (
							<View style={{ width: '100%' }}>
								<OrderSummary
									giftCardOptions={formattedGiftCardOptions}
									setShowOrderSummary={setShowOrderSummary}
									navigation={navigation}
									resetSelections={resetGiftCardSelections}
								/>
							</View>
						)}
					</Stack>
				</View>
			</Box>
			{Platform.OS === 'web' && <CopyrightFooter />}
		</>
	)
}
